import { env } from 'environments'
import type { AllSetsProgressResponse, SetBucket, SetProgress, SetBucketRequirements } from '@artblocks/shared-types'
import { AchievementSetFragment, SetBucketProjectDetailFragment } from '@/gql/generated/graphql'
import { USER_REVALIDATE_TAG } from '@consts/common'
import { createTagWithVal } from './common'

/**
 * Fetches all sets progress data for a given user ID
 * @param userId - The user ID to fetch sets progress for
 * @returns Promise that resolves to AllSetsProgressResponse
 */
export async function fetchAllSetsProgress(userId: string | number): Promise<AllSetsProgressResponse> {
  const allSetsEndpoint = `${env.CORE_API_URL}/sets/all-sets-progress?userId=${userId}`

  const response = await fetch(allSetsEndpoint, {
    next: {
      tags: [createTagWithVal(USER_REVALIDATE_TAG, userId.toString())]
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch all sets progress: ${response.status}`)
  }

  const data: AllSetsProgressResponse = await response.json()

  return data
}

/**
 * Fetches set progress data for a specific set and user
 * @param userId - The user ID to fetch progress for
 * @param setSlug - The slug of the set to fetch progress for
 * @returns Promise that resolves to SetProgress
 */
export async function fetchSetProgress(userId: string, setSlug: string): Promise<SetProgress> {
  const setProgressEndpoint = `${env.CORE_API_URL}/sets/set-progress?userId=${userId}&setSlug=${setSlug}`

  const response = await fetch(setProgressEndpoint, {
    next: {
      tags: [createTagWithVal(USER_REVALIDATE_TAG, userId)]
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch set progress: ${response.status}`)
  }

  const data: SetProgress = await response.json()

  return data
}

export function getSetImageUrl(set?: AchievementSetFragment): string | null {
  if (!set) return null

  // Use static images from public folder for each set
  switch (set.slug) {
    case 'curated':
      return '/images/sets/curated.jpg'
    case 'curated-series-1-8':
      return '/images/sets/series-1-8.jpg'
    case 'pace-x-art-blocks':
      return '/images/sets/ab-x-pace.jpg'
    case 'bright-moments-x-art-blocks':
      return '/images/sets/ab-x-bm.jpg'
    case 'collaborations':
      return '/images/sets/collaborations.jpg'
    case 'explorations':
      return '/images/sets/explorations.jpg'
    case 'factory':
      return '/images/sets/factory.jpg'
    case 'playground':
      return '/images/sets/playground.jpg'
    case 'presents':
      return '/images/sets/presents.jpg'
    case 'studio':
      return '/images/sets/studio.jpg'
    case 'curated-series-1':
      return '/images/sets/series-1.jpg'
    case 'curated-series-2':
      return '/images/sets/series-2.jpg'
    case 'curated-series-3':
      return '/images/sets/series-3.jpg'
    case 'curated-series-4':
      return '/images/sets/series-4.jpg'
    case 'curated-series-5':
      return '/images/sets/series-5.jpg'
    case 'curated-series-6':
      return '/images/sets/series-6.jpg'
    case 'curated-series-7':
      return '/images/sets/series-7.jpg'
    case 'curated-series-8':
      return '/images/sets/series-8.jpg'
    case 'art-blocks-500':
      return '/images/sets/ab-500.jpg'
    case 'chromie-squiggle-full':
      return '/images/sets/chromie-squiggle-full.png'
    case 'chromie-squiggle-type':
      return '/images/sets/chromie-squiggle-type.png'
    case 'fidenza-color':
      return '/images/sets/fidenza-color.png'
    case 'friendship-bracelets':
      return '/images/sets/friendship-bracelets.png'
    case 'ab500-foundations':
      return '/images/sets/ab500-foundations.png'

    default: {
      // Fallback to the original logic using bucket 0
      const bucket = set.set_buckets?.[0]
      return bucket?.project?.featured_token?.[0]?.media_url ?? null
    }
  }
}

export type EnrichedSetBucket = SetBucket & {
  project?: SetBucketProjectDetailFragment // Optional for non-project bucket types (placeholder, vertical, tag, trait)
  requirements_json?: SetBucketRequirements // JSON object containing bucket-specific requirements
  isHeader?: never
}

/**
 * Helper function to pluralize feature names if they aren't already plural
 * @param featureName - The feature name to potentially pluralize
 * @returns The pluralized feature name
 */
function pluralizeFeatureName(featureName: string): string {
  const lowerCase = featureName.toLowerCase()

  // If it already ends with common plural suffixes, return as-is
  if (lowerCase.endsWith('s') || lowerCase.endsWith('es') || lowerCase.endsWith('ies')) {
    return featureName
  }

  // Simple pluralization: just add 's'
  return featureName + 's'
}

/**
 * Gets the appropriate label for set buckets based on their types and requirements
 * @param buckets - Array of enriched set buckets
 * @returns The appropriate label string for the buckets
 */
export function getSetBucketsLabel(buckets: EnrichedSetBucket[]): string {
  if (buckets.length === 0) {
    return 'Collections'
  }

  // Get the types of all buckets
  const bucketTypes = buckets.map((bucket) => bucket.type || 'project')
  const uniqueTypes = [...new Set(bucketTypes)]

  // Special case: if we have project and placeholder types, return "Collections"
  if (uniqueTypes.length === 2 && uniqueTypes.includes('project') && uniqueTypes.includes('placeholder')) {
    return 'Collections'
  }

  // If we have mixed types, default to "Requirements"
  if (uniqueTypes.length > 1) {
    return 'Requirements'
  }

  const primaryType = uniqueTypes[0]

  switch (primaryType) {
    case 'project':
      return 'Collections'

    case 'vertical':
      return 'Categories'

    case 'trait': {
      // For trait buckets, check if all buckets have the same feature name
      const featureNames = new Set<string>()

      for (const bucket of buckets) {
        try {
          const requirements = bucket.requirements_json
          if (requirements && 'features' in requirements) {
            // Get all feature names from this bucket
            Object.keys(requirements.features).forEach((featureName) => {
              featureNames.add(featureName)
            })
          }
        } catch (error) {
          // If we can't parse requirements, fall back to "Requirements"
          return 'Requirements'
        }
      }

      // If all buckets check exactly the same single feature, use that feature name
      if (featureNames.size === 1) {
        const featureName = Array.from(featureNames)[0]
        return pluralizeFeatureName(featureName)
      }

      // If different features or multiple features, use "Attributes"
      return 'Attributes'
    }

    default:
      // For any other bucket type, default to "Requirements"
      return 'Requirements'
  }
}
